// The goals bar: the counts a store works toward each day (repairs, phones bought, activations,
// phones sold, wholesale, bills paid, accessories), their targets, and the candle each one shows.
// Pure logic shared by the server (GET /api/goals, GET /api/goals/detail) and the screens (the bar,
// its panel with the click-through details, and Settings → Goals).

export type GoalKey = 'repairs' | 'phones_bought' | 'activations' | 'phones_sold' | 'wholesale' | 'bills_paid' | 'accessories';

export const GOAL_KEYS: GoalKey[] = ['repairs', 'phones_bought', 'activations', 'phones_sold', 'wholesale', 'bills_paid', 'accessories'];

export const GOAL_LABELS: Record<GoalKey, string> = {
  repairs: 'Repairs',
  phones_bought: 'Phones bought',
  activations: 'Activations',
  phones_sold: 'Phones sold',
  wholesale: 'Wholesale',
  bills_paid: 'Bills paid',
  accessories: 'Accessories',
};

/** What each count is, for Settings → Goals. */
export const GOAL_HELP: Record<GoalKey, string> = {
  repairs: 'Repair tickets checked in, one per device. Warranty re-checks and tickets cancelled as a mistake don’t count.',
  phones_bought: 'Trade-ins and phones bought for cash, one per phone. Voided ones don’t count.',
  activations: 'Activations rung up in sales. Refunded ones don’t count.',
  phones_sold: 'Phones (IMEI or serial devices) sold to customers in completed sales. Refunded sales and wholesale don’t count.',
  wholesale: 'Phones sent to a wholesale buyer. An undone wholesale doesn’t count.',
  bills_paid: 'Phone bills paid at the counter, one per payment. Refunded ones don’t count.',
  accessories: 'Accessories sold, counted by the piece: cases, screen protectors, chargers, cables, car mounts, speakers and anything else under the Accessories category. Refunded pieces don’t count.',
};

/** The names and help for a kind of shop: a mechanic's work orders, a computer shop's computers. A phone shop keeps the ones above. */
export function goalWords(industry: string | null | undefined): { labels: Record<GoalKey, string>; help: Record<GoalKey, string>; thing: string; things: string } {
  if (industry !== 'computer' && industry !== 'vacuum' && industry !== 'auto') return { labels: GOAL_LABELS, help: GOAL_HELP, thing: 'phone', things: 'phones' };
  const thing = industry === 'computer' ? 'computer' : industry === 'vacuum' ? 'vacuum' : 'vehicle';
  const things = `${thing}s`;
  const Things = things[0].toUpperCase() + things.slice(1);
  const auto = industry === 'auto';
  return {
    thing,
    things,
    labels: { repairs: auto ? 'Work orders' : 'Repairs', phones_bought: `${Things} bought`, activations: 'Activations', phones_sold: `${Things} sold`, wholesale: 'Wholesale', bills_paid: 'Bills paid', accessories: 'Accessories' },
    help: {
      repairs: auto
        ? 'Work orders written up, one per vehicle. Warranty re-checks and work orders cancelled as a mistake don’t count.'
        : `Repair tickets checked in, one per ${thing}. Warranty re-checks and tickets cancelled as a mistake don’t count.`,
      phones_bought: `Trade-ins and ${things} bought for cash, one per ${thing}. Voided ones don’t count.`,
      activations: GOAL_HELP.activations,
      phones_sold: `${Things} (serial-numbered) sold to customers in completed sales. Refunded sales and wholesale don’t count.`,
      wholesale: `${Things} sent to a wholesale buyer. An undone wholesale doesn’t count.`,
      bills_paid: GOAL_HELP.bills_paid,
      accessories: 'Accessories sold, counted by the piece: anything under the Accessories category. Refunded pieces don’t count.',
    },
  };
}

/** Counts that can carry a target. The others show just the number. */
export const TARGETABLE: GoalKey[] = ['repairs', 'phones_bought', 'activations', 'accessories'];

export type CandleStatus = 'amazing' | 'hit' | 'under' | 'far' | 'none';

export interface GoalTarget {
  /** Daily target; null means this count has no target (shows just the number). */
  daily: number | null;
  /** Daily "amazing" level; null means a target with no amazing level. */
  amazing: number | null;
}

export interface GoalsSettings {
  targets: Record<GoalKey, GoalTarget>;
  /** Counts the owner took off the bar (on top of the ones the shop never uses). */
  hidden: GoalKey[];
}

/** The starting targets: repairs 15 (amazing 20), phones bought 12, activations 5 (amazing 7); accessories has none until the owner sets one. */
export const DEFAULT_TARGETS: Record<GoalKey, GoalTarget> = {
  repairs: { daily: 15, amazing: 20 },
  phones_bought: { daily: 12, amazing: null },
  activations: { daily: 5, amazing: 7 },
  phones_sold: { daily: null, amazing: null },
  wholesale: { daily: null, amazing: null },
  bills_paid: { daily: null, amazing: null },
  accessories: { daily: null, amazing: null },
};

/** "$1,234.50" (a minus sign in front when negative). */
export function formatMoney(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const v = Math.abs(Math.round(cents));
  return `${sign}$${Math.floor(v / 100).toLocaleString('en-US')}.${String(v % 100).padStart(2, '0')}`;
}

export const DEFAULT_GOALS_SETTINGS: GoalsSettings = { targets: DEFAULT_TARGETS, hidden: [] };

export const CANDLE_COLORS = {
  amazing: '#16A34A',
  amazingBorder: '#E0A100',
  hit: '#16A34A',
  under: '#0057FF',
  far: '#C42127',
} as const;

export const STATUS_LABELS: Record<CandleStatus, string> = {
  amazing: 'Amazing',
  hit: 'Target hit',
  under: 'Under target',
  far: 'Far from target',
  none: '',
};

/**
 * The candle for one count:
 *  - at or above amazing      → 'amazing' (full green, gold border and star)
 *  - at or above target       → 'hit' (full green)
 *  - at least half the target → 'under' (blue, filled count / target)
 *  - below half               → 'far' (red, filled count / target)
 *  - no target                → 'none' (just the number)
 */
export function candleStatus(count: number, target: number | null, amazing: number | null): CandleStatus {
  if (!target || target <= 0) return 'none';
  if (amazing != null && amazing > 0 && count >= amazing) return 'amazing';
  if (count >= target) return 'hit';
  if (count * 2 >= target) return 'under';
  return 'far';
}

/** How full the candle is drawn: count / target, capped at 1 (full once the target is hit). */
export function candleFill(count: number, target: number | null): number {
  if (!target || target <= 0) return 0;
  if (count >= target) return 1;
  return Math.max(0, Math.min(1, count / target));
}

/**
 * The pill on a tile: "3 to go" (today), "54 behind" (this month, where the target is the pace
 * so far), "Target hit", "Amazing". Empty for counts without a target.
 */
export function pillText(count: number, target: number | null, amazing: number | null, range: 'today' | 'month' = 'today'): string {
  const status = candleStatus(count, target, amazing);
  if (status === 'none') return '';
  if (status === 'amazing') return 'Amazing';
  if (status === 'hit') return 'Target hit';
  const left = target! - count;
  return range === 'month' ? `${left} behind` : `${left} to go`;
}

export function daysInMonth(year: number, month1to12: number): number {
  return new Date(Date.UTC(year, month1to12, 0)).getUTCDate();
}

export interface MonthTargets {
  /** Daily target × days so far this month (today included). */
  target: number | null;
  amazing: number | null;
  /** Daily target × every day in the month: the month goal. */
  goal: number | null;
  goalAmazing: number | null;
  daysSoFar: number;
  daysInMonth: number;
}

/**
 * Month targets: the daily target times the days so far (today included), and the month goal: the
 * daily target times the days in the month. Oct 17 with 15 a day: target 255, amazing (20 a day)
 * 340; the month goal is 465 and 620.
 */
export function monthTargets(t: GoalTarget, year: number, month1to12: number, dayOfMonth: number): MonthTargets {
  const dim = daysInMonth(year, month1to12);
  const soFar = Math.max(1, Math.min(dim, dayOfMonth));
  const mul = (v: number | null, days: number) => (v && v > 0 ? v * days : null);
  return { target: mul(t.daily, soFar), amazing: mul(t.amazing, soFar), goal: mul(t.daily, dim), goalAmazing: mul(t.amazing, dim), daysSoFar: soFar, daysInMonth: dim };
}

/** Local calendar parts of an instant in a time zone, so "today" is midnight to midnight store time. */
export function localParts(at: Date, timeZone: string): { year: number; month: number; day: number; weekday: string; monthName: string } {
  const fmt = new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'short' });
  const parts: Record<string, string> = {};
  for (const p of fmt.formatToParts(at)) parts[p.type] = p.value;
  const monthName = new Intl.DateTimeFormat('en-US', { timeZone, month: 'short' }).format(at);
  return { year: Number(parts.year), month: Number(parts.month), day: Number(parts.day), weekday: parts.weekday, monthName };
}

function hourMinuteIn(at: Date, timeZone: string): { hour: number; minute: number } {
  const parts: Record<string, string> = {};
  for (const p of new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', minute: 'numeric', hourCycle: 'h23' }).formatToParts(at)) parts[p.type] = p.value;
  return { hour: Number(parts.hour) % 24, minute: Number(parts.minute) };
}

/** Epoch ms of local midnight on a calendar day in the time zone (DST-safe). */
export function localMidnightUtc(year: number, month1to12: number, day: number, timeZone: string): number {
  let guess = Date.UTC(year, month1to12 - 1, day, 0, 0, 0);
  for (let i = 0; i < 3; i++) {
    const p = localParts(new Date(guess), timeZone);
    const hm = hourMinuteIn(new Date(guess), timeZone);
    const asUtc = Date.UTC(p.year, p.month - 1, p.day, hm.hour, hm.minute);
    const diff = asUtc - Date.UTC(year, month1to12 - 1, day, 0, 0, 0);
    if (diff === 0) break;
    guess -= diff;
  }
  return guess;
}

/** Today in the store's time zone: from local midnight to the next local midnight, as epoch ms. */
export function dayBounds(at: Date, timeZone: string): { start: number; end: number } {
  const { year, month, day } = localParts(at, timeZone);
  const start = localMidnightUtc(year, month, day, timeZone);
  const next = new Date(Date.UTC(year, month - 1, day + 1));
  const end = localMidnightUtc(next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate(), timeZone);
  return { start, end };
}

/** This month in the store's time zone: from the 1st to the next local midnight (so it includes today). */
export function monthBounds(at: Date, timeZone: string): { start: number; end: number } {
  const { year, month } = localParts(at, timeZone);
  return { start: localMidnightUtc(year, month, 1, timeZone), end: dayBounds(at, timeZone).end };
}

/** "Sat, Oct 17" for the bar. */
export function shortDateLabel(at: Date, timeZone: string): string {
  const p = localParts(at, timeZone);
  return `${p.weekday}, ${p.monthName} ${p.day}`;
}

/** "October" for the panel's month row. */
export function monthLabel(at: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', { timeZone, month: 'long' }).format(at);
}

// ---------------------------------------------------------------- calendar days (the day picker behind a count)

/** A calendar day written as "2026-10-21": its year, month and day of the month. */
export function dateKeyParts(key: string): { year: number; month: number; day: number } {
  const [year, month, day] = key.split('-').map(Number);
  return { year, month, day };
}

/** True for a real calendar day written as "2026-10-21" (what the day picker sends). */
export function isDateKey(v: unknown): v is string {
  if (typeof v !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const { year, month, day } = dateKeyParts(v);
  return year >= 2000 && year <= 2200 && month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth(year, month);
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/** The calendar day an instant falls on in the time zone, as "2026-10-21". */
export function dateKeyOf(at: Date, timeZone: string): string {
  const p = localParts(at, timeZone);
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}`;
}

/** The calendar day `days` after one (negative for before): "2026-10-21" and 1 is "2026-10-22"; and -21 is "2026-09-30". */
export function addDays(key: string, days: number): string {
  const { year, month, day } = dateKeyParts(key);
  const d = new Date(Date.UTC(year, month - 1, day + days));
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

/** A calendar day in the store's time zone: from its local midnight to the next one, as epoch ms (DST-safe). */
export function dayBoundsOf(key: string, timeZone: string): { start: number; end: number } {
  const { year, month, day } = dateKeyParts(key);
  const next = dateKeyParts(addDays(key, 1));
  return { start: localMidnightUtc(year, month, day, timeZone), end: localMidnightUtc(next.year, next.month, next.day, timeZone) };
}

/** "Tue, Oct 20" for a calendar day. */
export function dateKeyLabel(key: string, timeZone: string): string {
  const { year, month, day } = dateKeyParts(key);
  return shortDateLabel(new Date(localMidnightUtc(year, month, day, timeZone) + 12 * 3_600_000), timeZone);
}

// ---------------------------------------------------------------- settings (Settings → Goals)

const isKey = (v: unknown): v is GoalKey => typeof v === 'string' && (GOAL_KEYS as string[]).includes(v);

function cleanLevel(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  if (!Number.isInteger(n) || n < 0 || n > 10_000) return null;
  return n === 0 ? null : n;
}

/** Reads the saved goals settings (JSON in the settings table); anything odd falls back to the defaults. */
export function parseGoalsSettings(raw: string | null | undefined): GoalsSettings {
  const out: GoalsSettings = { targets: { ...DEFAULT_TARGETS }, hidden: [] };
  if (!raw) return out;
  let j: any;
  try {
    j = JSON.parse(raw);
  } catch {
    return out;
  }
  if (!j || typeof j !== 'object') return out;
  for (const k of TARGETABLE) {
    const t = j.targets?.[k];
    if (!t || typeof t !== 'object') continue;
    const daily = cleanLevel(t.daily);
    const amazing = cleanLevel(t.amazing);
    out.targets[k] = { daily, amazing: daily && amazing && amazing > daily ? amazing : null };
  }
  if (Array.isArray(j.hidden)) out.hidden = j.hidden.filter(isKey);
  return out;
}

/** Checks what the owner typed in Settings → Goals. Returns what's wrong, or null. */
export function goalsSettingsProblem(input: unknown): string | null {
  if (!input || typeof input !== 'object') return 'Send the targets.';
  const j = input as { targets?: unknown; hidden?: unknown };
  if (j.targets !== undefined) {
    if (!j.targets || typeof j.targets !== 'object') return 'Send the targets.';
    for (const [k, v] of Object.entries(j.targets as Record<string, unknown>)) {
      if (!isKey(k)) return `Unknown count: ${k}.`;
      if (!TARGETABLE.includes(k)) {
        if (v && typeof v === 'object' && (cleanLevel((v as any).daily) || cleanLevel((v as any).amazing))) return `${GOAL_LABELS[k]} has no target: it shows just the number.`;
        continue;
      }
      if (!v || typeof v !== 'object') return `Check the ${GOAL_LABELS[k]} target.`;
      const t = v as { daily?: unknown; amazing?: unknown };
      const bad = (x: unknown) => x !== null && x !== undefined && x !== '' && (!Number.isInteger(Number(x)) || Number(x) < 0 || Number(x) > 10_000);
      if (bad(t.daily) || bad(t.amazing)) return `${GOAL_LABELS[k]}: use whole numbers (0 clears the target).`;
      const daily = cleanLevel(t.daily);
      const amazing = cleanLevel(t.amazing);
      if (amazing && !daily) return `${GOAL_LABELS[k]}: set a daily target before an amazing level.`;
      if (amazing && daily && amazing <= daily) return `${GOAL_LABELS[k]}: the amazing level has to be above the daily target.`;
    }
  }
  if (j.hidden !== undefined && (!Array.isArray(j.hidden) || !j.hidden.every(isKey))) return 'Pick which counts to hide from the list.';
  return null;
}

// ---------------------------------------------------------------- what GET /api/goals sends

export interface GoalMoney {
  /** What the wholesale buyers paid. */
  revenue_cents: number;
  /** What the phones cost the store. */
  cost_cents: number;
  /** Wholesale price minus cost. */
  profit_cents: number;
}

export interface GoalRow {
  key: GoalKey;
  label: string;
  count: number;
  target: number | null;
  amazing: number | null;
  /** This month only: the month goal (daily × days in the month). */
  goal: number | null;
  goal_amazing: number | null;
  status: CandleStatus;
  status_label: string;
  fill: number;
  pill: string;
  /** "5 trade-ins · 7 cash", "Cost and profit are for the owner only", "Cases 5 · Screen protectors 3"… */
  detail: string;
  breakdown?: { trade_ins: number; cash: number };
  /** Wholesale cost and profit: only ever sent to the owner. */
  money?: GoalMoney;
  /** Bills paid: what customers paid toward their bills, and the store's service fees. */
  bills?: { amount_cents: number; fee_cents: number };
  /** Accessories: pieces sold and what they brought in, with the categories selling most (up to three). */
  accessories?: { pieces: number; sales_cents: number; top: { name: string; pieces: number }[] };
}

export interface GoalsRange {
  range: 'today' | 'month';
  from: number;
  to: number;
  goals: GoalRow[];
}

// ---------------------------------------------------------------- what GET /api/goals/detail sends (a tracker, clicked)

/** One thing behind a count: a ticket, a phone bought, an activation, a sale line, a bill payment, a wholesale. */
export interface GoalDetailItem {
  /** When it happened, epoch ms. */
  at: number;
  /** "#1042" (ticket), "Receipt 100231", "Buy #1007". */
  ref: string;
  /** What it was: "Apple iPhone 13 128GB", "Activation · T-Mobile (New line)", "Clear Case · iPhone 15". */
  title: string;
  /** More about it: the issue, the plan, the account's last digits, cost and profit (owner only)… */
  sub: string;
  /** The customer (or the wholesale buyer). */
  who: string;
  /** The employee who rang it up or checked it in. */
  by: string;
  /** A short tag: "Trade-in", "Cash buy", "Checked in", "Ready"… */
  tag: string;
  /** Pieces, for accessories (a line of 3 cases is 3). */
  pieces: number | null;
  /** What it was worth: the price, the offer, the bill amount; null when there's no money on it. */
  amount_cents: number | null;
  /** Wholesale, the owner only: what the phone cost the store, and the price minus that. Never sent to anyone else. */
  cost_cents?: number;
  profit_cents?: number;
  /** Wholesale, everyone: the phone as its wholesale sticker prints it (IMEI, cost and sold price), so anyone can print it. */
  phone?: { brand: string; model: string; storage: string | null; color: string | null; carrier: string | null; locked: boolean; imei: string | null; serial: string | null };
  /** Wholesale, everyone: the cost the sticker prints (the owner's list also has cost_cents and profit_cents). */
  sticker_cost_cents?: number;
  /** The phone's record in inventory (phones bought, sold or sent wholesale): to edit it, put it on sale or print its sticker from the list. */
  device?: { id: string; status: string; price_cents: number; battery_health: number | null; title: string } | null;
}

export interface GoalDetailGroup {
  name: string;
  pieces: number;
  /** What those pieces brought in (the price paid, minus what was given back). */
  sales_cents: number;
}

/** One day of the history behind a count: the calendar day, how it reads ("Tue, Oct 20") and the count that day. */
export interface GoalDetailDay {
  date: string;
  label: string;
  count: number;
}

export interface GoalDetailResponse {
  key: GoalKey;
  label: string;
  /** 'today', 'day' (the day picker: yesterday, last Tuesday…) or 'month'. */
  range: 'today' | 'month' | 'day';
  /** The calendar day listed ("2026-10-21"; today's, for this month) and how it reads ("Tue, Oct 21"). */
  date: string;
  date_label: string;
  /** Today in the store's time zone, so the screen knows what "yesterday" is and where the day picker stops. */
  today_date: string;
  /** The last two weeks up to the day listed, oldest first, with this count for each day: the history behind the day picker. */
  days: GoalDetailDay[];
  from: number;
  to: number;
  store: string;
  store_name: string;
  timezone: string;
  /** The same number the bar shows for this count and range. */
  count: number;
  /** The amounts of the items, added up (owner-only money never comes here for anyone else). */
  total_cents: number | null;
  /** What the total is: "sold", "offered", "in bills"… */
  total_label: string;
  /** Newest first, up to `limit`; `more` is how many older ones aren't listed. */
  items: GoalDetailItem[];
  limit: number;
  more: number;
  /** Accessories only: what's selling most, by product and by category (most pieces first). */
  groups?: { by_product: GoalDetailGroup[]; by_category: GoalDetailGroup[] };
}

export interface GoalsResponse {
  show_bar: boolean;
  /** This person is the owner: sees cost and profit, can switch to all stores and edit targets. */
  owner: boolean;
  store: string;
  store_name: string;
  /** Stores this person can look at (the owner: every open store, plus 'all'). */
  stores: { id: string; name: string }[];
  can_pick_all: boolean;
  timezone: string;
  date: { label: string; day: number; days_in_month: number; month: string };
  today: GoalsRange | null;
  month: GoalsRange | null;
}
